/**
 * `OptionGrid` builtin surface — browse a set of offerings (plans, services,
 * products, rooms, classes). Cards explain by default; explicit selectable mode
 * stages a choice in the normal composer. Optional attachment actions stage
 * displayed objects in the composer. Prices and durations are supplied facts.
 */
import { useContext, type FC } from 'react';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { SurfaceHeader } from '@/app/lib/a2ui/blocks/SurfaceHeader.tsx';
import { OptionCardGrid, type OptionCard } from '@/app/lib/a2ui/blocks/OptionCardGrid.tsx';
import { BlockSkeleton } from '@/app/lib/a2ui/blocks/BlockSkeleton.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { useIntl } from 'react-intl';
import { messages } from '@/app/lib/localization/messages.ts';
import { contentExplorationContext, EXPLORE_CONTENT_ACTION } from '../content-exploration.ts';
import { AddToMessage } from '../blocks/AddToMessage.tsx';
import { AddToComparison } from '../blocks/AddToComparison.tsx';

interface OptionRecord {
  id: string;
  name: string;
  description?: string;
  price?: string;
  priceCaption?: string;
  imageUrl?: string;
  featured?: boolean;
  meta?: string;
}

function readOption(raw: unknown): OptionRecord | null {
  if (!isRecord(raw)) {
    return null;
  }
  const id = str(raw.id);
  const name = str(raw.name);
  if (!id || !name) {
    return null;
  }
  return {
    id,
    name,
    description: optStr(raw.explanation ?? raw.description),
    price: optStr(raw.price),
    priceCaption: optStr(raw.priceCaption),
    imageUrl: optStr(raw.imageUrl),
    featured: raw.featured === true,
    meta: optStr(raw.meta),
  };
}

function toOptionCard(option: OptionRecord, featuredLabel: string): OptionCard {
  return {
    id: option.id,
    title: option.name,
    description: option.description,
    value: option.price,
    valuePrefix: option.priceCaption,
    imageUrl: option.imageUrl,
    meta: option.meta,
    featured: option.featured,
    featuredLabel: option.featured ? featuredLabel : undefined,
  };
}

export const OptionGridSurface: FC<A2uiNodeViewProps> = ({ node }) => {
  const api = useContext(A2uiSurfaceContext);
  const intl = useIntl();
  const options = arr(node.props.options)
    .map(readOption)
    .filter((option): option is OptionRecord => option !== null);

  if (!Array.isArray(node.props.options) || options.length === 0) {
    return <BlockSkeleton variant="cards" />;
  }

  const handleSelect = (id: string) => {
    if (!api) {
      return;
    }
    const selected = options.find((option) => option.id === id);
    if (!selected) {
      return;
    }
    if (node.props.selectable === true) {
      api.addToMessage?.({
        id: node.id,
        label: selected.name,
        instruction: selected.name,
        context: contentExplorationContext(node, { ...selected }),
      });
    } else {
      api.dispatch(EXPLORE_CONTENT_ACTION, contentExplorationContext(node, { ...selected }));
    }
  };

  return (
    <div>
      <SurfaceHeader
        title={str(node.props.highlight ?? node.props.title)}
        subtitle={optStr(node.props.message ?? node.props.subtitle)}
      />
      <OptionCardGrid
        options={options.map((option) =>
          toOptionCard(option, intl.formatMessage(messages.featured)),
        )}
        onSelect={handleSelect}
        selectLabel={
          node.props.selectable === true ? intl.formatMessage(messages.addToMessage) : undefined
        }
        renderFooter={
          node.props.attachable === true || node.props.comparable === true
            ? (option) => {
                const source = options.find((item) => item.id === option.id);
                if (!source) return null;
                if (node.props.attachable === true)
                  return (
                    <AddToMessage
                      item={{
                        id: `${node.id}:${source.id}`,
                        label: source.name,
                        instruction: source.name,
                        context: contentExplorationContext(node, { ...source }),
                      }}
                    />
                  );
                return (
                  <AddToComparison
                    node={node}
                    id={source.id}
                    label={source.name}
                    selection={{ ...source }}
                  />
                );
              }
            : undefined
        }
      />
    </div>
  );
};
